
import React, { useMemo, useState } from 'react';
import { Link2, X, AlertCircle, Search } from 'lucide-react';
import { RegexConnectModalProps } from './types';

const RegexConnectModal: React.FC<RegexConnectModalProps> = ({ open, parentNode, candidates, onClose, onConfirm }) => {
  const [pattern, setPattern] = useState('');
  // Nodes the user has explicitly excluded from the current match set.
  const [excluded, setExcluded] = useState<Set<string>>(new Set());

  // Reset transient state whenever the modal is (re)opened.
  React.useEffect(() => {
    if (open) {
      setPattern('');
      setExcluded(new Set());
    }
  }, [open]);

  const { matches, error } = useMemo(() => {
    if (!pattern.trim()) return { matches: [] as typeof candidates, error: null as string | null };
    let regex: RegExp;
    try {
      regex = new RegExp(pattern, 'i');
    } catch (e) {
      return { matches: [] as typeof candidates, error: (e as Error).message };
    }
    return {
      matches: candidates.filter(n => regex.test(n.data.name || '')),
      error: null as string | null
    };
  }, [pattern, candidates]);

  if (!open) return null;

  const selectedIds = matches.filter(n => !excluded.has(n.id)).map(n => n.id);

  const toggle = (id: string) => {
    setExcluded(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  return (
    <div className="fixed inset-0 z-[200] flex items-center justify-center p-4">
      {/* Backdrop */}
      <div className="absolute inset-0 bg-slate-900/40 backdrop-blur-sm" onClick={onClose}></div>

      {/* Dialog */}
      <div className="relative bg-white rounded-2xl shadow-2xl w-full max-w-lg overflow-hidden animate-in fade-in zoom-in duration-200 border border-slate-200 flex flex-col max-h-[80vh]">
        <div className="p-6 pb-4 shrink-0">
          <div className="flex items-center justify-between mb-1">
            <h3 className="text-lg font-bold text-slate-900 flex items-center gap-2">
              <Link2 className="text-teal-500" />
              Connect by Regex
            </h3>
            <button onClick={onClose} className="p-1 text-slate-400 hover:bg-slate-100 rounded-full transition-colors">
              <X size={20} />
            </button>
          </div>
          <p className="text-xs text-slate-500 mb-4">
            Matching groups will be re-parented under{' '}
            <span className="font-semibold text-slate-700">{parentNode?.data.name || 'the selected group'}</span>.
          </p>

          <div className="relative">
            <div className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400">
              <Search size={16} />
            </div>
            <input
              autoFocus
              className={`w-full pl-9 pr-3 py-2.5 text-sm border rounded-lg focus:outline-none focus:ring-1 transition-all font-mono ${
                error
                  ? 'border-red-300 focus:ring-red-500/20 focus:border-red-500'
                  : 'border-slate-300 focus:ring-teal-500/20 focus:border-teal-500'
              }`}
              placeholder="e.g. ^Sales|North$"
              value={pattern}
              onChange={(e) => setPattern(e.target.value)}
            />
          </div>
          {error && (
            <div className="mt-2 flex items-center gap-1.5 text-xs text-red-600">
              <AlertCircle size={13} />
              <span>Invalid regex: {error}</span>
            </div>
          )}
          {!error && pattern.trim() && (
            <div className="mt-2 text-xs text-slate-500">
              <span className="font-bold text-slate-700">{selectedIds.length}</span> of {matches.length} matched group{matches.length === 1 ? '' : 's'} selected
            </div>
          )}
        </div>

        {/* Match list */}
        <div className="flex-1 overflow-y-auto px-6 min-h-[80px]">
          {pattern.trim() && !error && matches.length === 0 && (
            <div className="text-center text-sm text-slate-400 py-8">No groups match this pattern.</div>
          )}
          <ul className="space-y-1 pb-2">
            {matches.map(node => {
              const isSelected = !excluded.has(node.id);
              return (
                <li key={node.id}>
                  <label className="flex items-center gap-3 px-3 py-2 rounded-lg hover:bg-slate-50 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={isSelected}
                      onChange={() => toggle(node.id)}
                      className="w-4 h-4 rounded border-slate-300 text-teal-600 focus:ring-teal-500/30"
                    />
                    <span className={`text-sm truncate ${isSelected ? 'text-slate-700' : 'text-slate-400 line-through'}`}>
                      {node.data.name || '(unnamed)'}
                    </span>
                  </label>
                </li>
              );
            })}
          </ul>
        </div>

        <div className="p-6 pt-4 flex gap-3 shrink-0 border-t border-slate-100">
          <button
            onClick={onClose}
            className="flex-1 py-2.5 px-4 border border-slate-300 text-slate-600 font-bold text-sm rounded-xl hover:bg-slate-50 transition-colors"
          >
            Cancel
          </button>
          <button
            onClick={() => onConfirm(selectedIds)}
            disabled={selectedIds.length === 0}
            className={`flex-1 py-2.5 px-4 bg-teal-600 text-white font-bold text-sm rounded-xl transition-all shadow-lg shadow-teal-600/20 ${
              selectedIds.length === 0 ? 'opacity-50 cursor-not-allowed' : 'hover:bg-teal-700 active:scale-95'
            }`}
          >
            Connect {selectedIds.length > 0 ? `(${selectedIds.length})` : ''}
          </button>
        </div>
      </div>
    </div>
  );
};

export default RegexConnectModal;
